/**
 * Hook V2 — useCreateApprenant
 *
 * 3 modes :
 *  - createManual(data)        → POST /v2/apprenants
 *  - createFromPdf(pdfBase64)  → POST /v2/apprenants/from-pdf
 *  - createFromLead(leadId)    → POST /v2/apprenants/from-lead/:leadId
 *
 * Tous renvoient { id, isTest, message }. Le caller redirige ensuite
 * vers /apprenants/{id} pour afficher la fiche nouvellement créée.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api.js';
import type { ApprenantPatchBody } from './useApprenant.js';

export interface CreateApprenantResult {
  id: string;
  isTest: boolean;
  message: string;
  extraction?: unknown;
  inscriptionId?: string;
}

export interface ManualCreatePayload extends Partial<ApprenantPatchBody> {
  nom: string;
  prenom: string;
  email: string;
  isTest?: boolean;
  /**
   * ⭐⭐⭐ L'OFFRE DU CATALOGUE NATIF — 09/10/2026.
   *
   * L'écran envoyait `formationId`, un identifiant de l'ANCIENNE base que la
   * route de création ne lit nulle part : le choix se perdait, et le refus
   * réclamait « Formation » — un champ que cet écran n'offre pas.
   *
   *     ⛔ LE CHOIX QUE L'ÉCRAN FAIT FAIRE DOIT ARRIVER QUELQUE PART.
   *
   * ⚠️ Un AUTRE nom exprès : c'est un `OffreFormation.id`.
   */
  offreId?: string;
  /**
   * ⭐⭐⭐ LE FILET DU CATALOGUE — 09/10/2026.
   *
   * Le catalogue natif ne se reconstruit QUE sur demande. Une offre créée
   * depuis la dernière reprise n'apparaît pas dans la liste.
   *
   *     ⛔ UN ÉCRAN QUI N'OFFRE QU'UNE LISTE EST UNE IMPASSE LE JOUR OÙ LA
   *        LISTE N'A PAS CE QU'ON CHERCHE.
   *
   * ⚠️ Aucune règle nouvelle : le moteur accorde déjà la priorité à un
   *    intitulé saisi.
   */
  formation?: string;
}

export interface EdofOverrides {
  numeroDossier?: string;
  nom?: string;
  prenom?: string;
  email?: string;
  telephone?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  niveauScolaire?: string;
  formation?: string;
  certification?: string;
  dateDebut?: string;
  dateFin?: string;
  heuresTotal?: number;
  modalite?: string;
  montantTtc?: number;
  codeOffre?: string;
  codeAction?: string;
  statutCpf?: string;
}

export interface FromPdfPayload {
  pdfBase64: string;
  isTest?: boolean;
  /** Champs modifiés par l'user dans le formulaire de preview avant import */
  overrides?: EdofOverrides;
}

export interface FromLeadPayload {
  leadId: string;
  niveau?: string;
  formationId?: string;
  statutZoho?: string;
  isTest?: boolean;
}

export function useCreateApprenant(): {
  createManual: (payload: ManualCreatePayload) => Promise<CreateApprenantResult>;
  createFromPdf: (payload: FromPdfPayload) => Promise<CreateApprenantResult>;
  createFromLead: (payload: FromLeadPayload) => Promise<CreateApprenantResult>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['v2', 'apprenant'] });
    queryClient.invalidateQueries({ queryKey: ['v2', 'apprenants'] });
  };

  const manual = useMutation({
    mutationFn: (payload: ManualCreatePayload) =>
      api.post<CreateApprenantResult>('/api/v1/v2/apprenants', payload),
    onSuccess: invalidate,
  });
  const pdf = useMutation({
    mutationFn: (payload: FromPdfPayload) =>
      api.post<CreateApprenantResult>('/api/v1/v2/apprenants/from-pdf', payload),
    onSuccess: invalidate,
  });
  const lead = useMutation({
    mutationFn: (payload: FromLeadPayload) =>
      api.post<CreateApprenantResult>(`/api/v1/v2/apprenants/from-lead/${payload.leadId}`, payload),
    onSuccess: invalidate,
  });

  return {
    createManual: manual.mutateAsync,
    createFromPdf: pdf.mutateAsync,
    createFromLead: lead.mutateAsync,
    isPending: manual.isPending || pdf.isPending || lead.isPending,
  };
}

/**
 * Helper : convertit un File HTML en base64 (sans le préfixe data:).
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Lecture fichier échouée'));
        return;
      }
      resolve(result.replace(/^data:[^;]+;base64,/, ''));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
